import { replayQueuedActionsOnLoad } from "./offline-queue"

/** How often to poll for a new service worker (ms). */
export const SW_UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000

function shouldSkipServiceWorkerRegistration(): boolean {
  if (typeof window === "undefined") return false
  const { hostname, protocol } = window.location
  return (
    (hostname === "localhost" || hostname === "127.0.0.1") && protocol === "https:"
  )
}

function swScriptUrl(): string {
  const buildId = import.meta.env.VITE_BUILD_ID
  return buildId ? `/sw.js?build=${encodeURIComponent(buildId)}` : "/sw.js"
}

let reloadScheduled = false

function scheduleReloadOnControllerChange(): void {
  if (reloadScheduled) return
  reloadScheduled = true

  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => {
      window.location.reload()
    },
    { once: true }
  )
}

function activateWaitingWorker(registration: ServiceWorkerRegistration): void {
  const waiting = registration.waiting
  if (!waiting) return

  scheduleReloadOnControllerChange()
  waiting.postMessage({ type: "SKIP_WAITING" })
}

function notifyUpdateAvailable(registration: ServiceWorkerRegistration): void {
  window.dispatchEvent(
    new CustomEvent("sw-update-available", { detail: registration })
  )
}

function handleWaitingWorker(registration: ServiceWorkerRegistration): void {
  if (!registration.waiting || !navigator.serviceWorker.controller) return
  notifyUpdateAvailable(registration)
  activateWaitingWorker(registration)
}

export async function checkForServiceWorkerUpdate(
  registration: ServiceWorkerRegistration
): Promise<void> {
  try {
    await registration.update()
    handleWaitingWorker(registration)
  } catch {
    // Ignore transient update failures (offline, etc.)
  }
}

export function startServiceWorkerUpdatePolling(
  registration: ServiceWorkerRegistration
): () => void {
  const runCheck = () => {
    void checkForServiceWorkerUpdate(registration)
  }

  runCheck()

  const onVisible = () => {
    if (document.visibilityState === "visible") runCheck()
  }
  document.addEventListener("visibilitychange", onVisible)

  const intervalId = window.setInterval(runCheck, SW_UPDATE_CHECK_INTERVAL_MS)

  return () => {
    document.removeEventListener("visibilitychange", onVisible)
    window.clearInterval(intervalId)
  }
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null
  if (shouldSkipServiceWorkerRegistration()) return null

  try {
    const registration = await navigator.serviceWorker.register(swScriptUrl(), {
      scope: "/",
    })

    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing
      if (!newWorker) return
      newWorker.addEventListener("statechange", () => {
        if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
          handleWaitingWorker(registration)
        }
      })
    })

    handleWaitingWorker(registration)

    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "sync-success") {
        window.dispatchEvent(
          new CustomEvent("offline-action-synced", { detail: event.data })
        )
      }
    })

    return registration
  } catch (error) {
    if (shouldSkipServiceWorkerRegistration()) {
      console.debug("SW registration skipped on local HTTPS dev:", error)
      return null
    }
    console.error("SW registration failed:", error)
    return null
  }
}

export function initServiceWorkerLifecycle() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return

  let stopPolling: (() => void) | undefined

  window.addEventListener("load", () => {
    registerServiceWorker()
      .then((registration) => {
        if (registration) {
          stopPolling = startServiceWorkerUpdatePolling(registration)
        }
      })
      .catch(() => {})

    if (navigator.onLine) {
      replayQueuedActionsOnLoad().catch(() => {})
    }
  })

  window.addEventListener("online", () => {
    replayQueuedActionsOnLoad().catch(() => {})
  })

  window.addEventListener("beforeunload", () => {
    stopPolling?.()
  })
}
